import type { ProductVariantDraft } from "./types";
import { helperClass, inputClass, labelClass, sectionCard } from "./constants";

type VariantsSectionProps = {
  variants: ProductVariantDraft[];
  addVariant: () => void;
  removeVariant: (index: number) => void;
  updateVariant: (
    index: number,
    field: keyof ProductVariantDraft,
    value: string | boolean | File | null
  ) => void;
  removeVariantImage: (index: number) => void;
};

export const VariantsSection = ({
  variants,
  addVariant,
  removeVariant,
  updateVariant,
  removeVariantImage,
}: VariantsSectionProps) => (
  <section className={sectionCard}>
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
      <div>
        <h2 className="text-xl font-semibold text-[#5c2f2f]">
          Sizes / Variants
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Add one or more size options. Each size can have its own price, image,
          and shipping measurements.
        </p>
      </div>

      <button
        type="button"
        onClick={addVariant}
        className="px-4 py-2 rounded-xl bg-[#5c2f2f] text-white hover:bg-[#7a4444]"
      >
        + Add Size
      </button>
    </div>

    <div className="space-y-5">
      {variants.map((variant, index) => (
        <div
          key={index}
          className="rounded-2xl border border-gray-200 bg-[#fafafa] p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#5c2f2f]">
              Size #{index + 1}
            </h3>

            {variants.length > 1 && (
              <button
                type="button"
                onClick={() => removeVariant(index)}
                className="text-sm text-red-600 hover:underline"
              >
                Remove
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Size Label</label>
              <input
                type="text"
                value={variant.size_label}
                onChange={(e) =>
                  updateVariant(index, "size_label", e.target.value)
                }
                placeholder="Ex: 8 oz, 16 oz, 250 ml"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Price ($)</label>
              <input
                type="number"
                value={variant.price}
                onChange={(e) => updateVariant(index, "price", e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Weight (oz)</label>
              <input
                type="number"
                value={variant.weightOz}
                onChange={(e) => updateVariant(index, "weightOz", e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Height (in)</label>
              <input
                type="number"
                value={variant.height}
                onChange={(e) => updateVariant(index, "height", e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Length (in)</label>
              <input
                type="number"
                value={variant.length}
                onChange={(e) => updateVariant(index, "length", e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Width (in)</label>
              <input
                type="number"
                value={variant.width}
                onChange={(e) => updateVariant(index, "width", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className={labelClass}>Variant Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                updateVariant(index, "imageFile", e.target.files?.[0] || null)
              }
              className={inputClass}
            />
            <p className={helperClass}>
              Add this only when the size needs its own photo.
            </p>
          </div>

          {(variant.imageFile || variant.existingImageUrl) && (
            <div className="mt-4">
              <div className="relative inline-block rounded-xl border bg-white p-2">
                <img
                  src={
                    variant.imageFile
                      ? URL.createObjectURL(variant.imageFile)
                      : variant.existingImageUrl
                  }
                  alt={`Variant ${index + 1}`}
                  className="h-28 w-28 rounded-lg object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeVariantImage(index)}
                  className="absolute right-0 top-0 flex h-7 w-7 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-sm font-bold text-red-600 shadow hover:bg-red-50"
                  aria-label={`Remove variant ${index + 1} image`}
                >
                  x
                </button>
              </div>
            </div>
          )}

          <div className="mt-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={variant.is_active}
                onChange={(e) =>
                  updateVariant(index, "is_active", e.target.checked)
                }
              />
              Active size
            </label>
          </div>
        </div>
      ))}
    </div>
  </section>
);
